import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigation } from '@react-navigation/native';

import { Text } from '@/components/TextComponent';
import { ds } from '@/components/lib/design.system';
import { useNonWithdrawableVerificationCheck } from '@/feature/account-details/hooks/use-non-withdrawable-verification-check';
import { WithdrawVerificationOutcome } from '@/feature/account-details/utils/resolve-withdraw-verification-outcome';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { useTrustlyKycStore } from '@/feature/kyc/hooks/use-trustly-kyc-store';
import { useSocialWalletTranslation } from '@/hooks/use-social-wallet-translation';
import { navigateToKyc } from '@/navigation/navigation';
import { IdpvSource } from '@/navigation/types';

const NonWithdrawableVerificationCheck = () => {
    const tSocialWallet = useSocialWalletTranslation('account');
    return (
        <Text mb="s8" color={ds.colorToken.text.secondary} textAlign="center">
            {tSocialWallet('non_withdrawable_check')}
        </Text>
    );
};

/**
 * Starts the verification flow matching the current withdraw verification outcome:
 * the choice screen, the single available path, or the no-viable-path sheet.
 * No-ops when the outcome is 'cleared'.
 */
export const useStartWithdrawVerification = () => {
    const { t } = useTranslation(['account', 'common']);
    const navigation = useNavigation();
    const { showInfoSheet } = useAlerts();
    const { outcome } = useNonWithdrawableVerificationCheck();

    return useCallback(() => {
        switch (outcome) {
            case WithdrawVerificationOutcome.CHOICE:
                navigateToKyc(navigation, { screen: 'VerificationSelector' });
                break;
            case WithdrawVerificationOutcome.TRUSTLY_ONLY:
                showInfoSheet({
                    title: t('link_bank_account'),
                    description: <NonWithdrawableVerificationCheck />,
                    buttonLabel: t('trustly_link_bank_account_title'),
                    handlePress: () => {
                        useTrustlyKycStore.getState().setShouldProceedToWithdrawal(true);
                        navigateToKyc(navigation, { screen: 'TrustlyId' });
                    },
                    secondaryLabel: t('common:close'),
                });
                break;
            case WithdrawVerificationOutcome.IDPV_ONLY:
                BetrAnalytics.trackProductEvent(AnalyticsEvent.WITHDRAW_VERIFICATION_SELECTED_IDPV, { auto: true });
                navigateToKyc(navigation, { screen: 'KycScanId', params: { source: IdpvSource.WITHDRAW } });
                break;
            case WithdrawVerificationOutcome.BLOCKED:
                BetrAnalytics.trackProductEvent(AnalyticsEvent.WITHDRAW_VERIFICATION_BLOCKED);
                showInfoSheet({
                    title: t('verification_unavailable_title'),
                    description: t('verification_unavailable_description'),
                    buttonLabel: t('common:close'),
                });
                break;
        }
    }, [outcome, navigation, showInfoSheet, t]);
};
